import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, LinearProgress,
  TextField, Chip, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { IEncuesta } from '../../models/encuestas';
import { useEncuestas } from '../../hooks/useEncuestas';
import { Loader } from '../../components/Loader';

interface IDetalleEncuestaProps {
  idEncuesta: number;
  onBack: () => void;
}

interface IFlat {
  grupoId: number;
  grupoNombre: string;
  preguntaIdx: number;
  numPregunta: number;
  texto: string;
  helpText: string;
  tipo: number;
  opciones: { opcion: number; texto: string }[];
}

// tipo_Respueta: 4 = radio, 5 = checkbox, 6 = text
const TIPO_RADIO    = 4;
const TIPO_CHECKBOX = 5;
const TIPO_TEXT     = 6;

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const DetalleEncuesta = ({ idEncuesta, onBack }: IDetalleEncuestaProps) => {
  const { getDetalleEncuesta, saveEncuesta } = useEncuestas();
  const [encuesta, setEncuesta]   = useState<IEncuesta | null>(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [step, setStep]           = useState<'cover' | 'question' | 'done' | 'error'>('cover');
  const [errorMsg, setErrorMsg]   = useState('');
  const [current, setCurrent]     = useState(0);
  const [flat, setFlat]           = useState<IFlat[]>([]);
  const [answers, setAnswers]     = useState<Record<string, number | number[] | string>>({});

  useEffect(() => {
    setLoading(true);
    getDetalleEncuesta(idEncuesta).then(data => {
      setLoading(false);
      if (!data || data.length === 0) return;
      const enc = data[0];
      setEncuesta(enc);
      const questions: IFlat[] = [];
      (enc.grupos ?? []).forEach(grupo => {
        (grupo.preguntas ?? []).forEach((p, idx) => {
          questions.push({
            grupoId: grupo.id,
            grupoNombre: grupo.nombre,
            preguntaIdx: idx,
            numPregunta: p.num_Pregunta,
            texto: p.texto_Pregunta,
            helpText: p.help_Text,
            tipo: p.tipo_Respueta,
            opciones: p.opciones,
          });
        });
      });
      setFlat(questions);
    });
  }, [idEncuesta]);

  const key = (f: IFlat) => `${f.grupoId}_${f.numPregunta}`;

  const handleRadio = (f: IFlat, opcionId: number) => {
    setAnswers(prev => ({ ...prev, [key(f)]: opcionId }));
  };

  const handleCheckbox = (f: IFlat, opcionId: number) => {
    setAnswers(prev => {
      const current = (prev[key(f)] as number[]) ?? [];
      const exists  = current.includes(opcionId);
      return { ...prev, [key(f)]: exists ? current.filter(v => v !== opcionId) : [...current, opcionId] };
    });
  };

  const handleText = (f: IFlat, value: string) => {
    setAnswers(prev => ({ ...prev, [key(f)]: value }));
  };

  const buildXml = (): string => {
    let xml = '<rs>';
    flat.forEach(q => {
      const k = key(q);
      const val = answers[k];
      if (val === undefined || val === '') return;

      if (q.tipo === TIPO_RADIO) {
        xml += `<r><p>${q.numPregunta}</p><o>${val}</o></r>`;
      } else if (q.tipo === TIPO_CHECKBOX) {
        (val as number[]).forEach(opId => {
          xml += `<r><p>${q.numPregunta}</p><o>${opId}</o></r>`;
        });
      } else if (q.tipo === TIPO_TEXT) {
        xml += `<r><p>${q.numPregunta}</p><t>${escapeXml(val as string)}</t></r>`;
      }
    });
    xml += '</rs>';
    return xml;
  };

  const handleFinish = async () => {
    setSaving(true);
    const xml = buildXml();
    const result = await saveEncuesta(idEncuesta, xml);
    setSaving(false);

    if (result.success) {
      setStep('done');
    } else {
      setErrorMsg(result.message || 'No se pudo guardar la encuesta.');
      setStep('error');
    }
  };

  if (loading) return <Box mt={6}><Loader loadingText="Cargando encuesta..." /></Box>;
  if (!encuesta) return (
    <Box textAlign="center" mt={8}>
      <Typography color="error">No se pudo cargar la encuesta.</Typography>
      <Button onClick={onBack} sx={{ mt: 2 }}>Volver</Button>
    </Box>
  );

  const total    = flat.length;
  const progress = total > 0 ? ((current) / total) * 100 : 0;
  const q        = flat[current];
  const isLast   = current === total - 1;

  // ── COVER ──────────────────────────────────────────────────────────
  if (step === 'cover') return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9' }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
        px: 3, pt: 3, pb: 6,
        position: 'relative',
      }}>
        <IconButton onClick={onBack} sx={{ color: '#fff', mb: 1, ml: -1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}>
          {encuesta.nombre}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
          {total} pregunta{total !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box sx={{ px: 3, mt: -2 }}>
        <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 3, boxShadow: '0 4px 16px rgba(1,52,103,0.1)' }}>
          <Typography variant="body1" sx={{ color: '#444', mb: 3, lineHeight: 1.6 }}>
            Sus respuestas son confidenciales y nos ayudarán a mejorar nuestros servicios.
            Por favor responda con honestidad.
          </Typography>

          <Box sx={{ mb: 3 }}>
            {(encuesta.grupos ?? []).map(g => (
              <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#013467' }} />
                <Typography variant="body2" sx={{ color: '#555' }}>
                  {g.nombre} ({g.preguntas.length} pregunta{g.preguntas.length !== 1 ? 's' : ''})
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            fullWidth variant="contained"
            size="large"
            onClick={() => setStep('question')}
            sx={{
              backgroundColor: '#013467',
              borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 15,
              '&:hover': { backgroundColor: '#0a4a8a' },
            }}
          >
            Iniciar encuesta
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // ── DONE ───────────────────────────────────────────────────────────
  if (step === 'done') return (
    <Box sx={{
      minHeight: '100vh', backgroundColor: '#f4f6f9',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      px: 3,
    }}>
      <CheckCircleIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#013467', mb: 1, textAlign: 'center' }}>
        ¡Gracias por responder!
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', mb: 4 }}>
        Sus respuestas han sido registradas correctamente.
      </Typography>
      <Button
        variant="contained" onClick={onBack}
        sx={{ backgroundColor: '#013467', borderRadius: 2, px: 4 }}
      >
        Volver al listado
      </Button>
    </Box>
  );

  // ── ERROR ──────────────────────────────────────────────────────────
  if (step === 'error') return (
    <Box sx={{
      minHeight: '100vh', backgroundColor: '#f4f6f9',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      px: 3,
    }}>
      <ErrorOutlineIcon sx={{ fontSize: 72, color: '#c62828', mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#013467', mb: 1, textAlign: 'center' }}>
        No se pudo guardar
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', mb: 4 }}>
        {errorMsg}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}
          sx={{ borderRadius: 2, borderColor: '#013467', color: '#013467' }}>
          Volver al listado
        </Button>
        <Button variant="contained" onClick={() => { setStep('question'); setCurrent(flat.length - 1); }}
          sx={{ backgroundColor: '#013467', borderRadius: 2 }}>
          Reintentar
        </Button>
      </Box>
    </Box>
  );

  // ── QUESTION ───────────────────────────────────────────────────────
  const radioValue    = (answers[key(q)] as number)   ?? -1;
  const checkValues   = (answers[key(q)] as number[]) ?? [];
  const textValue     = (answers[key(q)] as string)   ?? '';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <Box sx={{ backgroundColor: '#013467', px: 2, pt: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={onBack} sx={{ color: '#fff', mr: 1, p: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', flex: 1 }}>
            {encuesta.nombre}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {current + 1} / {total}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4, borderRadius: 0,
            backgroundColor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#FFCC00' },
          }}
        />
      </Box>

      {/* Question card */}
      <Box sx={{ flex: 1, px: 2, pt: 2.5, pb: 12 }}>
        <Chip
          label={q.grupoNombre}
          size="small"
          sx={{ mb: 2, backgroundColor: '#e8f0fb', color: '#013467', fontWeight: 600, fontSize: 11 }}
        />

        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a2d4e', mb: 1, lineHeight: 1.4 }}>
          {q.texto}
        </Typography>

        {q.helpText && (
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
            {q.helpText}
          </Typography>
        )}

        {/* Radio */}
        {q.tipo === TIPO_RADIO && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(q.opciones ?? []).map(op => {
              const selected = radioValue === op.opcion;
              return (
                <Box
                  key={op.opcion}
                  onClick={() => handleRadio(q, op.opcion)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: 1.75, borderRadius: 2, cursor: 'pointer',
                    border: `2px solid ${selected ? '#013467' : '#dde3ee'}`,
                    backgroundColor: selected ? '#e8f0fb' : '#fff',
                    transition: 'all 0.15s',
                  }}
                >
                  <Box sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${selected ? '#013467' : '#bbb'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {selected && <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#013467' }} />}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400, color: selected ? '#013467' : '#333' }}>
                    {op.texto}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Checkbox */}
        {q.tipo === TIPO_CHECKBOX && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(q.opciones ?? []).map(op => {
              const checked = checkValues.includes(op.opcion);
              return (
                <Box
                  key={op.opcion}
                  onClick={() => handleCheckbox(q, op.opcion)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: 1.75, borderRadius: 2, cursor: 'pointer',
                    border: `2px solid ${checked ? '#013467' : '#dde3ee'}`,
                    backgroundColor: checked ? '#e8f0fb' : '#fff',
                    transition: 'all 0.15s',
                  }}
                >
                  <Box sx={{
                    width: 20, height: 20, borderRadius: 1, flexShrink: 0,
                    border: `2px solid ${checked ? '#013467' : '#bbb'}`,
                    backgroundColor: checked ? '#013467' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && <Box component="span" sx={{ color: '#fff', fontSize: 13, lineHeight: 1, mt: '-1px' }}>✓</Box>}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400, color: checked ? '#013467' : '#333' }}>
                    {op.texto}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Text */}
        {q.tipo === TIPO_TEXT && (
          <TextField
            fullWidth multiline rows={4}
            variant="outlined"
            placeholder="Escriba su respuesta aquí..."
            value={textValue}
            onChange={e => handleText(q, e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
                '&.Mui-focused fieldset': { borderColor: '#013467' },
              },
            }}
          />
        )}
      </Box>

      {/* Fixed bottom navigation */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        px: 2, py: 2,
        display: 'flex', gap: 2,
      }}>
        <Button
          fullWidth variant="outlined"
          onClick={() => current > 0 ? setCurrent(c => c - 1) : setStep('cover')}
          sx={{ borderRadius: 2, borderColor: '#013467', color: '#013467', fontWeight: 600 }}
        >
          Atrás
        </Button>
        <Button
          fullWidth variant="contained"
          disabled={saving}
          onClick={() => isLast ? handleFinish() : setCurrent(c => c + 1)}
          sx={{
            borderRadius: 2, fontWeight: 600,
            backgroundColor: isLast ? '#2e7d32' : '#013467',
            '&:hover': { backgroundColor: isLast ? '#1b5e20' : '#0a4a8a' },
          }}
        >
          {saving ? 'Guardando...' : isLast ? 'Finalizar' : 'Siguiente'}
        </Button>
      </Box>
    </Box>
  );
};
